import { memo, useState, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { adminNodeListCameras } from "../../../api/adminNodeCameras.ts";
import { NearbyViewModal } from "../../../components/place360/nearby/NearbyViewModal.tsx";
import { rowToNearbyCamera } from "../../../hooks/useCamera360Hotspots.ts";
import { PatrolAssetHotspotsOverlay } from "./PatrolAssetHotspotsOverlay.jsx";
import { hotspotHasOpenableContent } from "./patrolAssetHotspotContent";

export const PatrolAssetHotspotsLayer = memo(function PatrolAssetHotspotsLayer({
  enabled = true,
  hotspots = [],
  canvasRef,
  cameraRef,
  hostRef,
  onViewCameraOpenChange,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [openPopupIds, setOpenPopupIds] = useState(() => new Set());
  const [cameraById, setCameraById] = useState(() => new Map());
  const [viewCamera, setViewCamera] = useState(null);

  useEffect(() => {
    onViewCameraOpenChange?.(Boolean(viewCamera));
  }, [viewCamera, onViewCameraOpenChange]);

  useEffect(() => {
    let cancelled = false;
    void adminNodeListCameras()
      .then((rows) => {
        if (cancelled) return;
        const byId = new Map();
        for (const row of rows) {
          const nc = rowToNearbyCamera(row);
          if (nc) byId.set(nc.id, nc);
        }
        setCameraById(byId);
      })
      .catch(() => {
        if (!cancelled) setCameraById(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenContent = useCallback(
    (hotspot) => {
      const showType = String(hotspot?.showType ?? "").trim().toLowerCase();
      if (showType === "camera") {
        const linkedId = String(
          hotspot?.linkedCameraId ?? hotspot?.contentUrl ?? "",
        ).trim();
        const linked = linkedId ? cameraById.get(linkedId) : null;
        if (!linked) {
          toast.error("Linked camera is not available.");
          return;
        }
        setViewCamera(linked);
        setExpandedId(null);
        return;
      }

      if (showType === "api_url" || showType === "apiurl" || showType === "api") {
        return;
      }

      const hotspotId = hotspot?.id;
      if (!hotspotId) return;
      setOpenPopupIds((prev) => {
        const next = new Set(prev);
        if (next.has(hotspotId)) {
          next.delete(hotspotId);
        } else {
          next.add(hotspotId);
        }
        return next;
      });
      setExpandedId(null);
    },
    [cameraById],
  );

  const handleClosePopup = useCallback((id) => {
    setOpenPopupIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleHotspotPress = useCallback(
    (hotspot) => {
      const showType = String(hotspot?.showType ?? "").trim().toLowerCase();
      if (showType === "api_url") return;

      const hotspotId = hotspot?.id;
      const isContentOpen = hotspotId && openPopupIds.has(hotspotId);
      const expanded = expandedId === hotspotId;
      const canOpen = hotspotHasOpenableContent(hotspot);

      if (isContentOpen) {
        handleOpenContent(hotspot);
        return;
      }
      if (canOpen && !expanded) {
        handleOpenContent(hotspot);
        return;
      }
      setExpandedId((prev) => (prev === hotspotId ? null : hotspotId));
    },
    [expandedId, openPopupIds, handleOpenContent],
  );

  if (!enabled || !hotspots.length) return null;

  return (
    <>
      <PatrolAssetHotspotsOverlay
        enabled={enabled}
        hotspots={hotspots}
        canvasRef={canvasRef}
        cameraRef={cameraRef}
        hostRef={hostRef}
        expandedId={expandedId}
        openPopupIds={openPopupIds}
        onHotspotPress={handleHotspotPress}
        onClosePopup={handleClosePopup}
      />

      <NearbyViewModal
        camera={viewCamera}
        onClose={() => setViewCamera(null)}
      />
    </>
  );
});
